import { useEffect } from "react";
import { useLocation } from "react-router";
import { breakpoints } from "../theme";

/**
 * DAR EKRAN TAŞMA DEDEKTÖRÜ — yalnızca geliştirme kipinde.
 *
 * NEDEN VAR: telefon uyumunu sayfa sayfa GÖZLE aramak pahalı ve güvenilmez.
 * Bu kanca, dar ekranda kendi kaydırma kutusu OLMADIĞI hâlde içeriği taşan
 * kutuları konsola yazar — yani "bu ekran telefonda bozuk" sorusunu sen sayfayı
 * açar açmaz cevaplar.
 *
 * ÖLÇÜT: `scrollWidth > clientWidth` **ve** `overflow-x: visible`.
 * `overflow-x: auto/scroll` olan kutular kusur DEĞİLDİR: matris, ısı haritası ve
 * rota tablosu bilerek kendi içinde yatay kayar (kart yapmak onları okunmaz
 * kılardı). Bu ayrım olmadan dedektör her açılışta yalancı alarm üretirdi.
 */

const TOLERANS_PX = 2;

/** Konsolda tıklanabilir olsun diye kısa bir kimlik üretir. */
const kutuAdi = (el: Element): string => {
  const etiket = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const metin = (el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 40);
  return `${etiket}${id} — "${metin}"`;
};

const tara = () => {
  const bulgular: { kutu: string; tasma: number; el: Element }[] = [];

  document.querySelectorAll("main, main *").forEach((el) => {
    /*
     * clientWidth === 0 olan kutu ATLANIR. Kendi yerleşim kutusu olmayan
     * öğelerde (mutlak konumlu katmanlar, gizli input'lar) scrollWidth
     * içeriğin toplam genişliğini verir ve fark her zaman büyük çıkar.
     * Leaflet haritası tek başına 13 yalancı alarm üretiyordu — oysa sayfanın
     * kendisinde hiç taşma yoktu.
     */
    if (el.clientWidth === 0) return;

    const tasma = el.scrollWidth - el.clientWidth;
    if (tasma <= TOLERANS_PX) return;

    // Kendi kaydırma kutusu olanlar bilerek öyle; kusur sayılmaz.
    const overflowX = getComputedStyle(el).overflowX;
    if (overflowX !== "visible") return;

    /*
     * Taşmaya yalnızca MUTLAK KONUMLU çocuklar sebep oluyorsa kusur değildir:
     * bunlar akışın dışına bilerek yerleştirilir. Klasik örnek kaydırıcı
     * tutamağı — değer noktasına ortalandığı için uçta yarısı rayın dışında
     * kalır, her kaydırıcıda böyledir. Akıştaki içerik taşıyorsa hâlâ yakalanır.
     */
    const akistakiTasma = Array.from(el.children).some((cocuk) => {
      if (getComputedStyle(cocuk).position === "absolute") return false;
      return cocuk.getBoundingClientRect().right - el.getBoundingClientRect().right > TOLERANS_PX;
    });
    if (!akistakiTasma && el.children.length > 0) return;

    bulgular.push({ kutu: kutuAdi(el), tasma, el });
  });

  if (bulgular.length === 0) return;

  // En çok taşan önce: kök sebep genelde en dıştaki büyük taşmadır.
  bulgular.sort((a, b) => b.tasma - a.tasma);

  /* eslint-disable no-console */
  console.groupCollapsed(
    `%c[dar ekran] ${bulgular.length} kutu taşıyor (≤${breakpoints.mobile}px)`,
    "color:#EE4549;font-weight:600"
  );
  console.info(
    "Çoğu taşmanın sebebi flex zincirinde eksik `min-width: 0` — flex çocuğu " +
      "varsayılan olarak içeriğinin min-content genişliğinin altına inemez."
  );
  bulgular.slice(0, 15).forEach(({ kutu, tasma, el }) =>
    console.log(`+${tasma}px  ${kutu}`, el)
  );
  console.groupEnd();
  /* eslint-enable no-console */
};

const useOverflowDetector = () => {
  const location = useLocation();

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;

    const mq = window.matchMedia(`(max-width: ${breakpoints.mobile}px)`);
    let zamanlayici: ReturnType<typeof setTimeout>;

    // Veri geldikten sonra ölç: liste boşken hiçbir şey taşmıyor görünür.
    const planla = () => {
      if (!mq.matches) return;
      clearTimeout(zamanlayici);
      zamanlayici = setTimeout(tara, 800);
    };

    planla();
    mq.addEventListener("change", planla);
    window.addEventListener("resize", planla);

    return () => {
      clearTimeout(zamanlayici);
      mq.removeEventListener("change", planla);
      window.removeEventListener("resize", planla);
    };
  }, [location.pathname, location.search]);
};

export default useOverflowDetector;
